import { createFileRoute, useParams, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { isAxiosError } from "axios";
import { z } from "zod";

import type { OnboardingStepId, SetupResponse } from "@/types/onboarding";
import type { QueryClient } from "@tanstack/react-query";

import {
  allStepsDataQueryOptions,
  onboardingKeys,
  getFrameVariants,
  kitsQueryOptions,
  sampleProductsQueryOptions,
} from "@/api/onboarding";
import { StepBrandStyle } from "@/features/Onboarding/StepBrandStyle";
import { StepComplete } from "@/features/Onboarding/StepComplete";
import { StepComponents } from "@/features/Onboarding/StepComponents";
import { StepIntro } from "@/features/Onboarding/StepIntro";
import { StepOperation } from "@/features/Onboarding/StepOperation";
import { StepResults } from "@/features/Onboarding/StepResults";
import { StepScope } from "@/features/Onboarding/StepScope";
import { StepVoice } from "@/features/Onboarding/StepVoice";
import { DEFAULT_FRAME_VARIANTS } from "@/features/Onboarding/utils/defaultFrameVariants";
import {
  DEFAULT_KIT_ID,
  KIT_ID_PATTERN,
  entryKit,
  kitSearch,
} from "@/features/Onboarding/utils/kits";
import { rehydrateOnboardingCacheFromDraft } from "@/features/Onboarding/utils/onboardingDraftRehydrate";
import {
  ONBOARDING_STEP_ORDER,
  getFirstIncompleteStep,
  isStepAccessible,
} from "@/features/Onboarding/utils/steps";

// `kit` selects the brand kit the whole wizard operates on. Malformed values
// are stripped (not errors); a well-formed but unknown kit 404s server-side
// and is redirected back to the default kit in beforeLoad. Absent means the
// URL entered the wizard from outside (main nav, deep link, email editor) and
// beforeLoad resolves it to the treatment applied to the site.
const searchSchema = z.object({
  kit: z.string().regex(KIT_ID_PATTERN).optional().catch(undefined),
});

// The steps that mount the preview editor, and so the only ones whose product
// previews are worth warming.
const PREVIEW_STEPS: ReadonlySet<string> = new Set([
  "styling",
  "operation",
  "components",
  "voice",
]);

// Cache slot the warm-up parks its response in before the real domain is
// known. One constant is right: the key it goes into is already kit-scoped
// (`[siteId, kit, "pending"]`), so two treatments cannot collide, and two
// entries into this function for the SAME kit — the redirect hop, a
// step-to-step navigation — are meant to share the slot. That sharing is the
// dedupe: the second one rides the first's in-flight request instead of
// opening another. It stays distinct from any real domain so the step's own
// query, which only ever asks for a resolved domain, never reads a warm entry
// by accident.
const PENDING_DOMAIN = "pending";

export const Route = createFileRoute("/_auth/brand-onboarding/$siteId/$stepId")(
  {
    validateSearch: zodValidator(searchSchema),
    beforeLoad: async ({ params, context, search }) => {
      const { siteId, stepId } = params;
      const kit = search.kit ?? DEFAULT_KIT_ID;
      const { queryClient } = context as { queryClient: QueryClient };
      if (
        siteId &&
        stepId &&
        ONBOARDING_STEP_ORDER.includes(stepId as OnboardingStepId)
      ) {
        // Tile variants take no arguments and don't depend on the setup
        // response, so start the request now and await it where it's actually
        // used — the two used to run back to back for no reason, costing a
        // full RTT before the step could even mount. The no-op catch only
        // keeps a redirect thrown mid-flight from surfacing as an unhandled
        // rejection; the real await below still throws.
        const frameVariantsPromise = queryClient.ensureQueryData({
          queryKey: onboardingKeys.frameVariants(),
          queryFn: getFrameVariants,
        });
        frameVariantsPromise.catch(() => {});

        // `sample_products` takes the kit — the backend resolves the brand
        // from that kit's stored intro section — but not the domain, which
        // only scopes the cache entry. The kit is known here, so the request
        // does not have to wait for the setup response to reveal the domain:
        // on a cold load that put it a full round trip behind everything else,
        // which is why the products were the last thing to reach the canvas.
        // Start it alongside the setup request and file the response under the
        // real key once the domain is known.
        //
        // Only with a kit in the URL. Each treatment has its own website, so
        // warming before the kit is resolved would ask for the wrong one and
        // then hand that response to whichever treatment the redirect lands
        // on. The kit-less entry re-enters this function with `?kit=` set and
        // warms there instead.
        //
        // Skipped when the products for this kit's cached domain are already
        // there, so step-to-step navigation doesn't refetch.
        const cachedDomain = queryClient.getQueryData<SetupResponse>(
          allStepsDataQueryOptions(siteId, kit).queryKey,
        )?.intro?.domain;
        const warmProducts =
          !!search.kit &&
          PREVIEW_STEPS.has(stepId) &&
          !(
            cachedDomain &&
            queryClient.getQueryData(
              onboardingKeys.sampleProducts(siteId, cachedDomain, kit),
            )
          )
            ? queryClient.ensureQueryData(
                sampleProductsQueryOptions(siteId, PENDING_DOMAIN, kit),
              )
            : null;
        warmProducts?.catch(() => {});

        // A URL with no ?kit= entered the wizard from outside it (main nav,
        // deep link, email editor), so open the treatment that is actually
        // live on the site rather than the legacy default, and pin it in the
        // URL — every in-wizard link names its kit. Registry failures (backend
        // without kit support) fall back to the default.
        //
        // This costs one round trip the setup fetch below cannot start before:
        // we don't know which kit to ask for until it answers. `getKits` is a
        // small registry read (and `retry: false`, so a flaky endpoint can't
        // hold the wizard for a backoff cycle), and the setup request for the
        // resolved kit is started here rather than after the redirect, so only
        // the registry read itself is serialized — the redirect hop and the
        // router's re-validation overlap the setup request.
        if (!search.kit) {
          const kits = await queryClient
            .ensureQueryData(kitsQueryOptions(siteId))
            .catch(() => null);
          const entry = entryKit(kits);
          // The redirect re-enters beforeLoad, where ensureQueryData dedupes
          // onto this in-flight request instead of opening a second one.
          void queryClient.prefetchQuery(
            allStepsDataQueryOptions(siteId, entry),
          );
          throw redirect({
            to: "/brand-onboarding/$siteId/$stepId",
            params: { siteId, stepId },
            search: kitSearch(entry),
          });
        }

        // Pre-load the setup data and cache it for components to use
        let setupData;
        try {
          setupData = await queryClient.ensureQueryData(
            allStepsDataQueryOptions(siteId, kit),
          );
        } catch (error) {
          // Stale link or a kit deleted in another tab: the backend 404s with
          // "Unknown kit" — fall back to the default kit instead of erroring.
          if (
            kit !== DEFAULT_KIT_ID &&
            isAxiosError(error) &&
            error.response?.status === 404
          ) {
            throw redirect({
              to: "/brand-onboarding/$siteId/$stepId",
              params: { siteId, stepId },
              search: kitSearch(DEFAULT_KIT_ID),
            });
          }
          throw error;
        }

        // Restore any re-mapped step data the user hasn't saved yet from the
        // local draft, so a reload / new tab resumes without re-running the
        // ~90s generation. Skips a step only if it was saved after the re-map.
        rehydrateOnboardingCacheFromDraft(queryClient, siteId, setupData, kit);

        // Hand the warm-up's response to the key the step actually reads.
        // Fire-and-forget: the failure is swallowed here and surfaced by the
        // step's own query, which refetches under the real key.
        const domain = setupData?.intro?.domain;
        if (
          domain &&
          warmProducts &&
          !queryClient.getQueryData(
            onboardingKeys.sampleProducts(siteId, domain, kit),
          )
        ) {
          void warmProducts
            .then((products) =>
              queryClient.setQueryData(
                onboardingKeys.sampleProducts(siteId, domain, kit),
                products,
              ),
            )
            .catch(() => {});
        }

        // If the current step is not accessible then redirect to the first
        // incomplete step. The step being loaded IS the current step, so pass
        // it as currentStep — this is what lets the terminal `complete` /
        // `results` steps (gated to "only while you're on it") load directly.
        if (!isStepAccessible(stepId as OnboardingStepId, setupData, stepId)) {
          const firstIncompleteStep = getFirstIncompleteStep(setupData);
          if (firstIncompleteStep !== stepId) {
            throw redirect({
              to: "/brand-onboarding/$siteId/$stepId",
              params: { siteId, stepId: firstIncompleteStep },
              // Past the guard above, `kit` is always the URL's own kit.
              search: kitSearch(kit),
            });
          }
        }

        // Tile variants for creative generation (used across multiple steps),
        // requested in parallel with the setup data above.
        const frameVariants = await frameVariantsPromise;

        // Filter and order tile variants based on DEFAULT_FRAME_VARIANTS order
        const filteredFrames = DEFAULT_FRAME_VARIANTS.map((defaultVariant) => {
          const foundFrame = frameVariants?.find(
            (frame) => frame.key === defaultVariant.key,
          );
          return foundFrame || defaultVariant;
        });

        // Store filtered tiles in query cache for components to access
        queryClient.setQueryData(
          onboardingKeys.filteredFrameVariants(),
          filteredFrames,
        );
      } else {
        // Unknown step id. Keep a named kit; with none, stay kit-less so the
        // intro's own beforeLoad resolves the applied treatment.
        throw redirect({
          to: "/brand-onboarding/$siteId/$stepId",
          params: { siteId, stepId: "intro" },
          search: search.kit ? kitSearch(search.kit) : {},
        });
      }
    },
    component: RouteComponent,
  },
);

function RouteComponent() {
  const { stepId } = useParams({
    from: "/_auth/brand-onboarding/$siteId/$stepId",
  });

  return (
    <>
      {stepId === "intro" ? <StepIntro /> : null}
      {stepId === "styling" ? <StepBrandStyle /> : null}
      {stepId === "operation" ? <StepOperation /> : null}
      {stepId === "components" ? <StepComponents /> : null}
      {stepId === "voice" ? <StepVoice /> : null}
      {stepId === "scope" ? <StepScope /> : null}
      {stepId === "complete" ? <StepComplete /> : null}
      {stepId === "results" ? <StepResults /> : null}
    </>
  );
}
